import { ReactIcons, Stats } from "@kalyzee/escaet-ui";
import { ChatRef, CyclingQRCodeData, QRCode, QRCodeHooks, RefreshContainer } from "@kalyzee/kast-web-sdk/web-components";
import { Meeting, MeetingUserDataCallStatus } from "@kalyzee/kast-web-sdk/websocket";
import React, { ForwardedRef, useMemo } from "react";

import { MeetingSessionOvenMediaReceiverManagerBaseData } from "@kalyzee/kast-web-sdk";
import { OvenMediaReceiverWebRTCSession } from "@kalyzee/kast-web-sdk/webrtc";
import { BadBehaviorsCounter } from "../../../../components/behaviors/BadBehaviorsCounter";
import SecondaryScreenChat from "../../../../components/secondary-screens/chat/Chat";
import { SecondaryScreenSessions } from "../../../../components/secondary-screens/sessions/Sessions";
import { ScreenParams } from "../../../../hooks/screen";
import { DEFAULT_GROUP_PANEL_WIDTH, DEFAULT_PANEL_WIDTH, SlaveCarouselData } from "../meeting";
import styles from "./screen.module.css";

export interface SecondaryScreen1Props {
  meeting: Meeting;
  videoSessions: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData<any>>[];
  screenParams: ScreenParams;
  carouselData: SlaveCarouselData;
  qrCodeData?: CyclingQRCodeData;
  chatRef?: React.RefObject<ChatRef>;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export interface SecondaryScreen1Ref {}

export const SecondaryScreen1 = React.forwardRef(
  (
    { meeting, videoSessions, screenParams, carouselData, qrCodeData, chatRef, children, className, style }: SecondaryScreen1Props,
    forwardRef: ForwardedRef<SecondaryScreen1Ref>
  ) => {
    // Verifying expiration on slave side
    const qrCodeExpired = QRCodeHooks.useExpiration(qrCodeData?.data);

    const { nbOfRemoteStudents, nbOfStudentsOnSite } = useMemo(() => {
      let studentIds = new Set<string>();
      let remoteStudentIds = new Set<string>();
      let studentOnSiteIds = new Set<string>();

      const { sessions, usersData } = meeting;
      meeting.actors?.forEach((a) => {
        if (a.externalProvider !== "escaet:attendee") return;
        const user = a.user;
        if (!user) return;
        studentIds.add(user.id);
        if (usersData?.find((d) => d.userId === user.id && d.call?.status === MeetingUserDataCallStatus.ONSITE)) studentOnSiteIds.add(user.id);
        else if (sessions?.find((s) => s.user?.id === user.id)) remoteStudentIds.add(user.id);
      });

      const nbOfRemoteStudents = remoteStudentIds.size;
      const nbOfStudentsOnSite = studentOnSiteIds.size;
      return { nbOfRemoteStudents, nbOfStudentsOnSite };
    }, [meeting]);

    const renderRightPanel = () => {
      return (
        <div
          className={styles.column}
          style={{
            width: screenParams.panelWidth ?? (carouselData.data.mode === "groups" ? DEFAULT_GROUP_PANEL_WIDTH : DEFAULT_PANEL_WIDTH),
          }}
        >
          <div className={styles.dataContainer}>
            <RefreshContainer every={1000}>
              {() => (
                <div style={{ fontSize: "4.25rem", fontWeight: "bold", color: "white" }}>{`${new Date().toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  hour12: false, // Delete for 24-hour format
                  minute: "2-digit",
                })}`}</div>
              )}
            </RefreshContainer>
            <div style={{ color: "white" }}>
              <Stats classe={nbOfStudentsOnSite} distance={nbOfRemoteStudents} height={60} style={{ paddingRight: 20, "--font-side": "18px" } as any}></Stats>
            </div>
          </div>
          {qrCodeData?.active && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "0.5em",
              }}
            >
              <div
                style={{
                  width: 200,
                  height: 200,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "white",
                  borderRadius: "0.5em",
                  overflow: "hidden",
                }}
              >
                {qrCodeData.data && !qrCodeExpired ? (
                  <QRCode text={qrCodeData.data.value} style={{ width: "100%", height: "100%" }} />
                ) : (
                  <ReactIcons.Spinner style={{ width: "25%", height: "25%", color: "black" }} />
                )}
              </div>
              <div
                style={{
                  color: "white",
                  fontSize: "1.25em",
                  fontWeight: "bold",
                }}
              >
                Rejoindre le cours
              </div>
            </div>
          )}
          <BadBehaviorsCounter sessions={meeting.sessions} style={{ marginBottom: 10 }} />
          <SecondaryScreenChat className={styles.chatContainer} chatRef={chatRef} />
        </div>
      );
    };

    // ---------------------------------------------------------------------- //
    // ---------------------------  EFFECTS ----------------------------- //
    // ---------------------------------------------------------------------- //

    // ---------------------------------------------------------------------- //
    // ---------------------------  RENDER ----------------------------- //
    // ---------------------------------------------------------------------- //

    const classes = [];
    if (className) classes.push(className);
    return (
      <div className={classes.join(" ")} style={style}>
        <SecondaryScreenSessions meeting={meeting} videoSessions={videoSessions} screenParams={screenParams} carouselData={carouselData} />
        {renderRightPanel()}
        {children}
      </div>
    );
  }
);
